import { useMemo, useState, useEffect } from "react";
import { formatCurrencyForTable, formatPercentageForTable } from "../functions/quoteFunctions";
import { getGroupedData, itemStats } from "../functions/quoteOverviewCalculations";
import DataTableMatrix from "../../../components/DataTableMatrix";

const QuoteOverviewGeneralSalesforceTable = ({quoteItems, setExportData}) => {

    const [matrixData, setMatrixData] = useState([[]]);
    const year1QuoteItemStatsBySalesforceCode = useMemo(() => getGroupedData(quoteItems, "businessCOE", "", "", 1), [quoteItems]);
    const allQuoteItemStats = useMemo(() => itemStats(quoteItems, 1), [quoteItems]);

    const upfrontBusinessCoeCodes = useMemo(() => quoteItems.filter(item => item.isUpfront).map(item => item.upfrontBusinessCOE ?? "(Empty)"), [quoteItems]);
    const recurringBusinessCoeCodes = useMemo(() => quoteItems.filter(item => item.isRecurring).map(item => item.recurringBusinessCOE ?? "(Empty)"), [quoteItems]);
    const uniqueBusinessCoeCodes = useMemo(() => [...new Set([...upfrontBusinessCoeCodes, ...recurringBusinessCoeCodes])], [upfrontBusinessCoeCodes, recurringBusinessCoeCodes]);

    const rows = uniqueBusinessCoeCodes.map(x => ({
        key: x,
        label: x
    }));

    const cols = [
        {
            key: "year1Rev",
            label: "Yr1 Rev",
            dataType: "currency",
            render: function (row, col) {
                return formatCurrencyForTable(year1QuoteItemStatsBySalesforceCode.find(item => item.groupBy1 === row)?.["totalUpfrontAndRecurringPrice"] || 0);
            }
        },
        {
            key: "year1Gm",
            label: "Yr1 GM",
            dataType: "currency",
            render: function (row, col) {
                return formatCurrencyForTable(year1QuoteItemStatsBySalesforceCode.find(item => item.groupBy1 === row)?.["totalUpfrontAndRecurringMargin"] || 0);
            }
        },
        {
            key: "Yr1GmPercent",
            label: "Yr1 GM %",
            dataType: "percentage",
            render: function (row, col) {
                return formatPercentageForTable(year1QuoteItemStatsBySalesforceCode.find(item => item.groupBy1 === row)?.["totalUpfrontAndRecurringMarginPercentage"] || 0);
            }
        }
    ];

    const footerValuesForExport = useMemo(() => ([
        allQuoteItemStats.totalUpfrontAndRecurringPrice,
        allQuoteItemStats.totalUpfrontAndRecurringMargin,
        allQuoteItemStats.totalUpfrontAndRecurringMarginPercentage
    ]), [allQuoteItemStats]);

    const footerValuesForDisplay = useMemo(() => ([
        formatCurrencyForTable(footerValuesForExport[0], 0),
        formatCurrencyForTable(footerValuesForExport[1], 0),
        formatPercentageForTable(footerValuesForExport[2], 0)
    ]), [footerValuesForExport]);

    const convertDataTableToExportFormat = (matrixData, footerValuesForExport) => {
        const rowsForExport = [];

        rowsForExport.push([{value: "", type:"text", style: "header", borderBottom: true, borderRight: true}, ...cols.map(col => ({ value: col.label, type: "text", style: "header", borderBottom: true, align: "right" }))]);

        for (const row of rows) {
            if (matrixData[row.key] !== undefined) {
                console.log("Exporting row", row, matrixData[row.key]);
                    rowsForExport.push(
                        [{value: row.label, type:"text", style: "header", borderRight: true}, ...(cols.map(column => ({
                            value: matrixData[row.key][column.key].render ? matrixData[row.key][column.key].render(row.key, column.key) : matrixData[row.key][column.key].value,
                            dataType: column.dataType || "text"
                    })))]
                );
            }
        }

        rowsForExport.push([
            {value: "TOTAL:", align:"right", type:"text", style: "header", borderTop: true, borderRight: true},
            { value: footerValuesForExport[0].toString(), dataType: "currency", align: "right", style: "footer", borderTop: true },
            { value: footerValuesForExport[1].toString(), dataType: "currency", align: "right", style: "footer", borderTop: true },
            { value: footerValuesForExport[2].toString(), dataType: "percentage", align: "right", style: "footer", borderTop: true }
        ]);

        return rowsForExport;
    };
    
    useEffect(() => {      
 
        if (Object.keys(matrixData).length > 0) {
           setExportData(prev => {
                if (!matrixData) return prev;

                const otherSheets = prev?.sheets?.filter(sheet => sheet.id !== "general") || [];
                const newGeneralTabData = {
                    id: "general",
                    name: "General",
                    sortOrder: 1,
                    tables: (prev?.sheets?.find(sheet => sheet.id === "general")?.tables || []).filter(t => t.header !== "Business COE Overview")
                }

                newGeneralTabData.tables.push({
                    header: "Business COE Overview",
                    sortOrder: 2,
                    rows: convertDataTableToExportFormat(matrixData, footerValuesForExport),
                });

                return (
                {
                    ...prev,
                    sheets: [
                        ...otherSheets,
                        newGeneralTabData
                    ]
                })
            });
        } else {
            console.log("No matrix data to export", matrixData, Object.keys(matrixData));
        }
    }, [matrixData, footerValuesForExport, setExportData]);

    useEffect(() => {  
        if (quoteItems.length === 0) {
            setMatrixData([[]]);
        } else {
            const newMatrixData = {};   
                rows.forEach(row => {
                    newMatrixData[row.key] = {};

                    cols.forEach(col => {
                        newMatrixData[row.key][col.key] = {
                            render: col.render
                        };
                    });
                });

            setMatrixData(newMatrixData);
        }     
    }, [year1QuoteItemStatsBySalesforceCode]);

    return (
        <>
            <DataTableMatrix
                name="business-coe-overview"
                label="Business COE Overview"
                columns={cols}
                rows={rows}
                data={matrixData}
                footerValues={footerValuesForDisplay}
                width="auto"
            />
        </>
    );
}

export default QuoteOverviewGeneralSalesforceTable;